import React, { useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import Card from "../../../componentLibrary/Card";
import Spinner from "../../../componentLibrary/Spinner";
import {
  signupFunnelColors,
  segmentChartColors,
  DEFAULT_POINT_STYLE,
  DEFAULT_LEGEND_CONFIG,
  DEFAULT_X_SCALE_CONFIG,
  DEFAULT_Y_SCALE_CONFIG,
} from "../constants/chartColors";
import LineChartWidget from "./reusable/LineChartWidget";
import SectionTitle from "./reusable/SectionTitle";
import LoadingWrapper from "./reusable/LoadingWrapper";
import StatsCard from "./reusable/StatsCard";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// ── Types ──────────────────────────────────────────────────────────────────────

type SeriesKey =
  | "logins"
  | "pageViews"
  | "uniqueLogins"
  | "uniquePageViews"
  | "signups"
  | "firstLogins";

interface SeriesConfig {
  key: SeriesKey;
  label: string;
  borderColor: string;
  backgroundColor: string;
}

export interface ActivityChartData {
  labels: string[];
  logins: number[];
  uniqueLogins: number[];
  pageViews: number[];
  uniquePageViews: number[];
  signups: number[];
  firstLogins: number[];
}

export interface SignupTimelineData {
  labels: string[];
  codesEntered: number[];
  emailsVerified: number[];
  firstLogins: number[];
}

export interface FunnelData {
  stage1_codesEntered: number;
  stage2_emailsVerified: number;
  stage3_firstLogins: number;
  totalDropoffs: number;
  overallConversion: number;
}

export interface SegmentItem {
  segment: string;
  signups: number;
}

export interface UsageOverTimeViewProps {
  isSingleDay: boolean;
  // loading states
  isLoadingChart: boolean;
  isLoadingSignupSection: boolean;
  loadingLoginMetrics: boolean;
  loadingTutorial: boolean;
  loadingToolboxAdded: boolean;
  loadingToolboxUsage: boolean;
  loadingToolboxOpenEvents: boolean;
  // activity chart
  chartData: ActivityChartData;
  totalLogins: number;
  totalPageViews: number;
  avgDailyLogins: number;
  avgDailyPageViews: number;
  // signup timeline
  signupTimeline: SignupTimelineData;
  // signup summary
  totalSignups: number;
  totalRedemptions: number;
  conversionRate: number | null;
  // funnel
  funnel: FunnelData | null;
  // segments
  segments: SegmentItem[];
  // login metrics
  totalLoginsMetric: number;
  totalUsersMetric: number;
  avgLoginFrequency: number | null;
  minLoginCount: number;
  maxLoginCount: number;
  loginCountBuckets: { label: string; userCount: number }[];
  loginCountTotalUsers: number;
  loadingLoginCountDistribution: boolean;
  // tutorial
  tutorialViewedCount: number;
  tutorialVideoWatchedCount: number;
  // toolbox
  totalToolboxOpens: number;
  totalToolboxTopics: number;
  pagesAddedToToolbox: number;
  uniqueToolboxUsers: number;
  toolboxCreationStats: {
    _id: string;
    topicTitle: string;
    totalUsers: number;
    totalOpens: number;
  }[];
  toolboxOpenEvents: {
    _id: string;
    topicTitle: string;
    totalOpens: number;
    sources?: { source: string; openCount: number; uniqueUserCount: number }[];
  }[];
  toolboxOpenEventsTotalOpens: number;
  toolboxOpenEventsTotalTopics: number;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const SERIES: SeriesConfig[] = [
  {
    key: "logins",
    label: "Sign-ins",
    borderColor: "rgb(139, 92, 246)",
    backgroundColor: "rgba(139, 92, 246, 0.08)",
  },
  {
    key: "uniqueLogins",
    label: "Unique Users (Sign-ins)",
    borderColor: "rgb(16, 185, 129)",
    backgroundColor: "rgba(16, 185, 129, 0.08)",
  },
  {
    key: "pageViews",
    label: "Page Views",
    borderColor: "rgb(59, 130, 246)",
    backgroundColor: "rgba(59, 130, 246, 0.08)",
  },
  {
    key: "uniquePageViews",
    label: "Unique Page Viewers",
    borderColor: "rgb(245, 158, 11)",
    backgroundColor: "rgba(245, 158, 11, 0.08)",
  },
  {
    key: "signups",
    label: "Sign-ups",
    borderColor: "rgb(239, 68, 68)",
    backgroundColor: "rgba(239, 68, 68, 0.08)",
  },
  {
    key: "firstLogins",
    label: "First Logins",
    borderColor: "rgb(14, 165, 233)",
    backgroundColor: "rgba(14, 165, 233, 0.08)",
  },
];

const DEFAULT_ACTIVE: Record<SeriesKey, boolean> = {
  logins: true,
  uniqueLogins: false,
  pageViews: true,
  uniquePageViews: false,
  signups: true,
  firstLogins: false,
};

const formatSegmentName = (segment: string) => {
  if (segment === "nosegment") return "No Segment";
  return segment
    .split(/(?=[A-Z])/)
    .join(" ")
    .replace(/^\w/, (c) => c.toUpperCase());
};

const UsageOverTimeView: React.FC<UsageOverTimeViewProps> = ({
  isSingleDay,
  isLoadingChart,
  isLoadingSignupSection,
  loadingLoginMetrics,
  loadingTutorial,
  loadingToolboxAdded,
  loadingToolboxUsage,
  loadingToolboxOpenEvents,
  chartData,
  totalLogins,
  totalPageViews,
  avgDailyLogins,
  avgDailyPageViews,
  signupTimeline,
  totalSignups,
  totalRedemptions,
  conversionRate,
  funnel,
  segments,
  totalLoginsMetric,
  totalUsersMetric,
  avgLoginFrequency,
  minLoginCount,
  maxLoginCount,
  loginCountBuckets,
  loginCountTotalUsers,
  loadingLoginCountDistribution,
  tutorialViewedCount,
  tutorialVideoWatchedCount,
  totalToolboxOpens,
  totalToolboxTopics,
  pagesAddedToToolbox,
  uniqueToolboxUsers,
  toolboxCreationStats,
  toolboxOpenEvents,
  toolboxOpenEventsTotalOpens,
  toolboxOpenEventsTotalTopics,
}) => {
  const [activeSeries, setActiveSeries] =
    useState<Record<SeriesKey, boolean>>(DEFAULT_ACTIVE);

  const toggleSeries = (key: SeriesKey) =>
    setActiveSeries((prev) => ({ ...prev, [key]: !prev[key] }));

  const datasets = useMemo(
    () =>
      SERIES.filter((s) => activeSeries[s.key]).map((s) => ({
        label: s.label,
        data: chartData[s.key] as number[],
        borderColor: s.borderColor,
        backgroundColor: s.backgroundColor,
        fill: true,
      })),
    [activeSeries, chartData],
  );

  const hasData =
    chartData.labels.length > 0 &&
    (chartData.logins.some((v) => v > 0) ||
      chartData.pageViews.some((v) => v > 0) ||
      chartData.signups.some((v) => v > 0));

  // ── Chart configs ────────────────────────────────────────────────────────────

  const signupTimelineChartData = {
    labels: signupTimeline.labels,
    datasets: [
      {
        label: "Code Entered",
        data: signupTimeline.codesEntered,
        borderColor: signupFunnelColors.codeEntered.rgb,
        backgroundColor: signupFunnelColors.codeEntered.rgbaLight,
        pointBackgroundColor: signupFunnelColors.codeEntered.rgb,
        pointHoverBorderColor: signupFunnelColors.codeEntered.rgb,
        ...DEFAULT_POINT_STYLE,
        fill: false,
        tension: 0.3,
      },
      {
        label: "Verification Email Clicked",
        data: signupTimeline.emailsVerified,
        borderColor: signupFunnelColors.emailVerified.rgb,
        backgroundColor: signupFunnelColors.emailVerified.rgbaLight,
        pointBackgroundColor: signupFunnelColors.emailVerified.rgb,
        pointHoverBorderColor: signupFunnelColors.emailVerified.rgb,
        ...DEFAULT_POINT_STYLE,
        fill: false,
        tension: 0.3,
      },
      {
        label: "First Login",
        data: signupTimeline.firstLogins,
        borderColor: signupFunnelColors.firstLogin.rgb,
        backgroundColor: signupFunnelColors.firstLogin.rgbaLight,
        pointBackgroundColor: signupFunnelColors.firstLogin.rgb,
        pointHoverBorderColor: signupFunnelColors.firstLogin.rgb,
        ...DEFAULT_POINT_STYLE,
        fill: false,
        tension: 0.3,
      },
    ],
  };

  const signupTimelineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    plugins: {
      legend: DEFAULT_LEGEND_CONFIG,
      title: { display: false },
      tooltip: {
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y}`,
        },
      },
    },
    scales: { x: DEFAULT_X_SCALE_CONFIG, y: DEFAULT_Y_SCALE_CONFIG },
  };

  const funnelChartData = {
    labels: ["Codes Entered", "Emails Verified", "First Logins"],
    datasets: [
      {
        data: [
          funnel?.stage1_codesEntered ?? 0,
          funnel?.stage2_emailsVerified ?? 0,
          funnel?.stage3_firstLogins ?? 0,
        ],
        backgroundColor: [
          signupFunnelColors.codeEntered.rgbaMedium,
          signupFunnelColors.emailVerified.rgbaMedium,
          signupFunnelColors.firstLogin.rgbaMedium,
        ],
        borderColor: [
          signupFunnelColors.codeEntered.rgbaFull,
          signupFunnelColors.emailVerified.rgbaFull,
          signupFunnelColors.firstLogin.rgbaFull,
        ],
        borderWidth: 1,
      },
    ],
  };

  const funnelChartOptions = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        callbacks: { label: (ctx: any) => `${ctx.label}: ${ctx.parsed.x}` },
      },
    },
    scales: { x: { beginAtZero: true } },
  };

  const segmentChartData = {
    labels: segments.map((item) => formatSegmentName(item.segment)),
    datasets: [
      {
        data: segments.map((item) => item.signups),
        backgroundColor: segmentChartColors.backgroundColors,
        borderColor: segmentChartColors.borderColors,
        borderWidth: 1,
      },
    ],
  };

  const segmentChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "right" as const },
      title: { display: false },
      tooltip: {
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (ctx: any) => {
            const total = (ctx.dataset.data as number[]).reduce(
              (a, b) => a + b,
              0,
            );
            const pct =
              total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : "0.0";
            return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
    },
  };

  const loginCountBarColor = "rgba(139, 92, 246, 0.75)";
  const loginCountBarBorder = "rgb(139, 92, 246)";

  const loginCountDistributionChartData = useMemo(
    () => ({
      labels: loginCountBuckets.map((b) => b.label),
      datasets: [
        {
          label: "Users",
          data: loginCountBuckets.map((b) => b.userCount),
          backgroundColor: loginCountBuckets.map(() => loginCountBarColor),
          borderColor: loginCountBuckets.map(() => loginCountBarBorder),
          borderWidth: 1,
        },
      ],
    }),
    [loginCountBuckets],
  );

  const loginCountDistributionChartOptions = useMemo(
    () => ({
      // Bins on the x-axis, counts on the y-axis (vertical bars).
      indexAxis: "x" as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: {
          callbacks: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            label: (ctx: any) => {
              const n = ctx.parsed?.y ?? ctx.parsed;
              const pct =
                loginCountTotalUsers > 0
                  ? ((n / loginCountTotalUsers) * 100).toFixed(1)
                  : "0.0";
              return `${n} users (${pct}%)`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "category" as const,
          ticks: { maxRotation: 45, minRotation: 0 },
        },
        y: {
          type: "linear" as const,
          beginAtZero: true,
          ticks: { precision: 0 },
          title: { display: true, text: "Users" },
        },
      },
    }),
    [loginCountTotalUsers],
  );

  return (
    <div className="space-y-4">
      {/* ── 1. Activity over time chart ────────────────────────────────────── */}
      <Card className="p-5">
        <SectionTitle>
          {isSingleDay ? "Hourly Activity" : "Activity Over Time"}
        </SectionTitle>

        <div className="flex flex-wrap gap-2 mb-5 justify-center">
          {SERIES.map((s) => {
            const isActive = activeSeries[s.key];
            return (
              <button
                key={s.key}
                onClick={() => toggleSeries(s.key)}
                className={`
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
                  border transition-all duration-150 select-none
                  ${
                    isActive
                      ? "text-white shadow-sm"
                      : "bg-white text-gray-500 border-gray-300 hover:border-gray-400"
                  }
                `}
                style={
                  isActive
                    ? {
                        backgroundColor: s.borderColor,
                        borderColor: "white",
                        outline: `2px solid ${s.borderColor}`,
                      }
                    : undefined
                }
              >
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0 border border-white"
                  style={{ backgroundColor: s.borderColor }}
                />
                {s.label}
              </button>
            );
          })}
        </div>

        {isLoadingChart ? (
          <div className="flex justify-center items-center h-72">
            <Spinner />
          </div>
        ) : !hasData ? (
          <div className="flex justify-center items-center h-72 text-gray-500 text-sm">
            No activity data found for the selected period.
          </div>
        ) : datasets.length === 0 ? (
          <div className="flex justify-center items-center h-72 text-gray-400 text-sm">
            Select at least one series above to display the chart.
          </div>
        ) : (
          <LineChartWidget
            labels={chartData.labels}
            datasets={datasets}
            xAxisLabel={isSingleDay ? "Hour" : "Date"}
            yAxisLabel="Count"
            height={380}
            hideLegend
          />
        )}
        <p className="text-xs text-gray-500 mt-3 text-center">
          Toggle series to show or hide them. All metrics respect the active
          segment and date filters.
        </p>
      </Card>

      {/* ── 2. Activity summary stats ──────────────────────────────────────── */}
      <StatsCard
        title={
          isSingleDay ? "Hourly Activity Summary" : "Daily Activity Summary"
        }
        loading={isLoadingChart}
        stats={[
          { label: "Total Sign-ins", value: totalLogins },
          { label: "Total Page Views", value: totalPageViews },
          {
            label: `Avg ${isSingleDay ? "Hourly" : "Daily"} Sign-ins`,
            value: avgDailyLogins,
          },
          {
            label: `Avg ${isSingleDay ? "Hourly" : "Daily"} Page Views`,
            value: avgDailyPageViews,
          },
        ]}
      />

      {/* ── 3. Login frequency stats ──────────────────────────────────────── */}
      <StatsCard
        title="Login Statistics"
        loading={loadingLoginMetrics}
        gridCols="grid-cols-2 md:grid-cols-5"
        stats={[
          { label: "Total Logins", value: totalLoginsMetric },
          { label: "Total Users", value: totalUsersMetric },
          {
            label: "Avg Login Frequency",
            value: avgLoginFrequency?.toFixed(2) ?? "—",
          },
          { label: "Min Logins", value: minLoginCount },
          { label: "Max Logins", value: maxLoginCount },
        ]}
      />

      <Card className="p-5">
        <SectionTitle>Users by sign-in count</SectionTitle>
        {loadingLoginCountDistribution ? (
          <div className="flex justify-center items-center h-72">
            <Spinner />
          </div>
        ) : loginCountTotalUsers === 0 ? (
          <div className="flex justify-center items-center h-48 text-gray-500 text-sm">
            No sign-ins in the selected period for the selected filters.
          </div>
        ) : (
          <div className="h-96 min-h-[20rem] w-full">
            <Bar
              data={loginCountDistributionChartData}
              options={loginCountDistributionChartOptions}
            />
          </div>
        )}
      </Card>

      {/* ── 4. Sign-up summary stats ───────────────────────────────────────── */}
      <StatsCard
        title="Sign-up Summary"
        loading={isLoadingSignupSection}
        stats={[
          { label: "Total Sign-ups", value: totalSignups },
          { label: "Codes Entered", value: totalRedemptions },
          {
            label: "Conversion Rate",
            value:
              conversionRate != null ? `${conversionRate.toFixed(1)}%` : "—",
          },
          { label: "Total Drop-offs", value: funnel?.totalDropoffs ?? 0 },
        ]}
      >
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-gray-700 mb-2">
            <strong>What this shows:</strong>
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li>
              <strong>Total Sign-ups:</strong> Users who completed registration
              in the selected period
            </li>
            <li>
              <strong>Codes Entered:</strong> Users who redeemed access codes in
              the same period
            </li>
            <li>
              <strong>Conversion Rate:</strong> Should be close to 100% since
              users must redeem a code to sign up
            </li>
            <li>
              <strong>Drop-offs:</strong> Users who redeemed codes but
              didn&apos;t complete registration
            </li>
          </ul>
        </div>
      </StatsCard>

      {/* ── 5. Sign-ups over time line chart ──────────────────────────────── */}
      <Card className="p-5">
        <SectionTitle>
          {isSingleDay ? "Sign-ups By Hour" : "Sign-ups Over Time"}
        </SectionTitle>
        <LoadingWrapper loading={isLoadingSignupSection}>
          <div className="h-64 sm:h-80 w-full">
            <Line
              data={signupTimelineChartData}
              options={signupTimelineOptions}
            />
          </div>
          <p className="text-xs text-gray-600 mt-3 text-center">
            Tracks the 3-stage signup journey over time: users entering codes,
            verifying emails, and completing first login.
          </p>
        </LoadingWrapper>
      </Card>

      {/* ── 6. Conversion funnel + sign-ups by segment ────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5">
          <SectionTitle>Conversion Funnel</SectionTitle>
          <LoadingWrapper loading={isLoadingSignupSection}>
            <div className="h-48">
              <Bar data={funnelChartData} options={funnelChartOptions} />
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Completion Rate:</span>
                <span className="font-semibold text-green-600">
                  {funnel?.overallConversion.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Drop-off Rate:</span>
                <span className="font-semibold text-red-600">
                  {(100 - (funnel?.overallConversion ?? 0)).toFixed(1)}%
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-3 text-center">
              Shows the percentage of people who complete sign-up after entering
              an access code.
            </p>
          </LoadingWrapper>
        </Card>

        <Card className="p-5">
          <SectionTitle>Sign-ups by Segment</SectionTitle>
          {isLoadingSignupSection ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : segments.length > 0 ? (
            <>
              <div className="h-48 flex items-center justify-center">
                <Doughnut
                  data={segmentChartData}
                  options={segmentChartOptions}
                />
              </div>
              <div className="mt-4 space-y-1">
                {segments.map((item) => (
                  <div
                    key={item.segment}
                    className="flex justify-between text-sm"
                  >
                    <span className="text-gray-600">
                      {formatSegmentName(item.segment)}:
                    </span>
                    <span className="font-semibold">{item.signups}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-600 mt-3 text-center">
                Distribution of sign-ups across different user segments.
              </p>
            </>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">
              No segment data available
            </div>
          )}
        </Card>
      </div>

      {/* ── 7. New user tutorial ──────────────────────────────────────────── */}
      <StatsCard
        title="New User Tutorial"
        loading={loadingTutorial}
        gridCols="grid-cols-2"
        stats={[
          { label: "Watched Tutorial", value: tutorialViewedCount },
          { label: "Watched Video Tutorial", value: tutorialVideoWatchedCount },
        ]}
      />

      {/* ── 8. Toolbox stats ──────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 p-5 w-full border-2 border-gray-200 rounded-lg">
        <h3 className="text-center m-0 mb-3 text-gray-700 text-lg underline">
          Toolbox
        </h3>

        {loadingToolboxAdded ||
        loadingToolboxUsage ||
        loadingToolboxOpenEvents ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Toolbox Creation Stats */}
            <div className="flex flex-col gap-4">
              <h4 className="text-center m-0 mb-2 text-gray-600 text-base font-semibold">
                Toolbox Creation Stats
              </h4>

              {/* Creation Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-500">
                    {totalToolboxOpens}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Total Opens</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-500">
                    {totalToolboxTopics}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Active Topics
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-500">
                    {totalToolboxTopics > 0
                      ? (totalToolboxOpens / totalToolboxTopics).toFixed(1)
                      : "0"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Avg Opens/Topic
                  </div>
                </div>
              </div>

              {/* Most Created Toolboxes */}
              {toolboxCreationStats.length > 0 && (
                <div className="bg-gray-100 rounded-lg p-4 shadow-sm">
                  <h5 className="m-0 mb-3 text-gray-700 text-sm font-medium">
                    Most Created Toolboxes (by unique users)
                  </h5>
                  <div className="space-y-2">
                    {toolboxCreationStats.slice(0, 5).map((item) => (
                      <div
                        key={item._id}
                        className="flex text-sm justify-between py-2 border-b border-gray-200 last:border-b-0"
                      >
                        <span className="flex-1 mr-3 whitespace-nowrap overflow-hidden text-ellipsis">
                          {item.topicTitle}
                        </span>
                        <span className="text-gray-600 font-medium">
                          {item.totalUsers} users
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {toolboxCreationStats.length === 0 && (
                <div className="text-center text-gray-500 py-4 text-sm">
                  No toolbox creation data available.
                </div>
              )}
            </div>

            {/* Toolbox Open Events */}
            <div className="flex flex-col gap-4">
              <h4 className="text-center m-0 mb-2 text-gray-600 text-base font-semibold">
                Toolbox Open Events
              </h4>

              {/* Open Events Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-500">
                    {toolboxOpenEventsTotalOpens}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Total Opens</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-500">
                    {toolboxOpenEventsTotalTopics}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Toolboxes Opened
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-500">
                    {toolboxOpenEvents.reduce((sum, e) => {
                      const s = e.sources?.find((s) => s.source === "explore");
                      return sum + (s?.openCount || 0);
                    }, 0)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">From Explore</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-500">
                    {toolboxOpenEvents.reduce((sum, e) => {
                      const s = e.sources?.find((s) => s.source === "module");
                      return sum + (s?.openCount || 0);
                    }, 0)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">From Topic</div>
                </div>
              </div>

              {/* Most Opened Toolboxes */}
              {toolboxOpenEvents.length > 0 && (
                <div className="bg-gray-100 rounded-lg p-4 shadow-sm">
                  <h5 className="m-0 mb-3 text-gray-700 text-sm font-medium">
                    Most Opened Toolboxes
                  </h5>
                  <div className="space-y-2">
                    {toolboxOpenEvents.slice(0, 5).map((event) => {
                      const exploreCount =
                        event.sources?.find((s) => s.source === "explore")
                          ?.openCount || 0;
                      const topicRouteOpens =
                        event.sources?.find((s) => s.source === "module")
                          ?.openCount || 0;
                      return (
                        <div
                          key={event._id}
                          className="flex text-sm justify-between py-2 border-b border-gray-200 last:border-b-0"
                        >
                          <span className="flex-1 mr-3 whitespace-nowrap overflow-hidden text-ellipsis">
                            {event.topicTitle}
                          </span>
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-gray-600 font-medium">
                              {event.totalOpens} total opens
                            </span>
                            <div className="flex gap-2 text-xs text-gray-500">
                              <span>{exploreCount} explore</span>
                              <span>•</span>
                              <span>{topicRouteOpens} topic</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {toolboxOpenEvents.length === 0 && (
                <div className="text-center text-gray-500 py-4 text-sm">
                  No toolbox open events available.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Page stats row */}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-500">
              {pagesAddedToToolbox}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Pages Added to Toolbox
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-500">
              {uniqueToolboxUsers}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Unique Users (Toolbox)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageOverTimeView;
